import { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAlgumModalAberto } from '../contexts/ModalContext';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { pagamentoEmAberto } from '../utils/pagamentos';

const NAV_TODOS = [
  { to: '/', label: 'Painel', icon: '📊' },
  { to: '/financeiro', label: 'Financeiro', icon: '💰', adminOnly: true },
  { to: '/clientes', label: 'Clientes', icon: '👥' },
  { to: '/alertas',       label: 'Alertas',       icon: '🔔', adminOnly: true },
  { to: '/configuracoes', label: 'Configurações', icon: '⚙️', adminOnly: true },
];

function tocarBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.6);
  } catch {}
}

function usePagamentosPendentes(isAdmin) {
  const [qtd, setQtd] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/servicos').then(({ data }) => {
      setQtd(data.filter(pagamentoEmAberto).length);
    }).catch(() => {});
    const id = setInterval(() => {
      api.get('/servicos').then(({ data }) => {
        setQtd(data.filter(pagamentoEmAberto).length);
      }).catch(() => {});
    }, 120000);
    return () => clearInterval(id);
  }, [isAdmin]);
  return qtd;
}

function useAguardandoOrcamento(isAdmin) {
  const [pendentes, setPendentes] = useState(0);
  const vistos = useRef(new Set(JSON.parse(localStorage.getItem('os_vistas_adm') || '[]')));
  const navigate = useNavigate();

  const verificar = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get('/servicos');
      const novas = data.filter(s => s.status === 'aguardando_orcamento');
      setPendentes(novas.length);

      const novidades = novas.filter(s => !vistos.current.has(s._id));
      if (novidades.length > 0) {
        tocarBeep();
        novidades.forEach(s => {
          vistos.current.add(s._id);
          toast(t => (
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-gray-800">📤 Nova OS para aprovação</span>
              <span className="text-sm text-gray-600">OS #{s.numero} — {s.cliente?.nome}</span>
              <button
                onClick={() => { navigate(`/servicos/${s._id}`); toast.dismiss(t.id); }}
                className="mt-1 text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 self-start"
              >
                Ver OS
              </button>
            </div>
          ), { duration: 8000 });
        });
        localStorage.setItem('os_vistas_adm', JSON.stringify([...vistos.current]));
      }

      // Limpar IDs de OS que saíram do status (aprovadas/canceladas)
      const idsAtivos = new Set(novas.map(s => s._id));
      vistos.current.forEach(id => { if (!idsAtivos.has(id)) vistos.current.delete(id); });
      localStorage.setItem('os_vistas_adm', JSON.stringify([...vistos.current]));
    } catch {}
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    verificar();
    const interval = setInterval(verificar, 120000);
    return () => clearInterval(interval);
  }, [isAdmin, verificar]);

  return pendentes;
}

function useAlertaMecanico(isAdmin, usuario) {
  const vistos = useRef(new Set(JSON.parse(localStorage.getItem('os_vistas_mec') || '[]')));
  const navigate = useNavigate();

  const verificar = useCallback(async () => {
    if (isAdmin || !usuario?.nome) return;
    try {
      const { data } = await api.get('/servicos');
      // OS aprovadas pelo ADM atribuídas a este mecânico
      const aprovadas = data.filter(s => s.status === 'aprovado' && s.responsavel === usuario.nome);
      const novidades = aprovadas.filter(s => !vistos.current.has(s._id));
      if (novidades.length > 0) {
        tocarBeep();
        novidades.forEach(s => {
          vistos.current.add(s._id);
          toast(t => (
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-gray-800">✅ OS aprovada — pode iniciar!</span>
              <span className="text-sm text-gray-600">OS #{s.numero} — {s.cliente?.nome}</span>
              <button
                onClick={() => { navigate(`/servicos/${s._id}`); toast.dismiss(t.id); }}
                className="mt-1 text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 self-start"
              >
                Ver OS
              </button>
            </div>
          ), { duration: 10000 });
        });
        localStorage.setItem('os_vistas_mec', JSON.stringify([...vistos.current]));
      }
      // Limpar IDs de OS que saíram do status aprovado
      const idsAtivos = new Set(aprovadas.map(s => s._id));
      vistos.current.forEach(id => { if (!idsAtivos.has(id)) vistos.current.delete(id); });
      localStorage.setItem('os_vistas_mec', JSON.stringify([...vistos.current]));
    } catch {}
  }, [isAdmin, usuario, navigate]);

  useEffect(() => {
    if (isAdmin || !usuario) return;
    verificar();
    const interval = setInterval(verificar, 120000);
    return () => clearInterval(interval);
  }, [isAdmin, usuario, verificar]);
}

// Detecta tablet em orientação paisagem (não desktop, não celular) para recolher
// a sidebar fixa e ganhar espaço horizontal de visualização — vira um toggle
// via hambúrguer igual ao mobile, em vez de ocupar a tela permanentemente.
function useTabletPaisagem() {
  const query = '(orientation: landscape) and (min-width: 768px) and (max-width: 1366px)';
  const [tabletPaisagem, setTabletPaisagem] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = e => setTabletPaisagem(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return tabletPaisagem;
}

const REFRESH_INTERVAL = 180000; // recarrega a página a cada 180s

const PAGINAS_LISTAGEM = ['/', '/financeiro', '/clientes', '/alertas', '/configuracoes'];

function useAutoRefresh() {
  const { pathname } = useLocation();
  const ehListagem = PAGINAS_LISTAGEM.includes(pathname);
  // Lido de dentro do setInterval via ref: se recarregasse o efeito a cada
  // abre/fecha de modal, o timer reiniciaria e nunca completaria o ciclo
  // enquanto o usuário estivesse cadastrando algo.
  const modalAbertoRef = useRef(false);
  modalAbertoRef.current = useAlgumModalAberto();

  useEffect(() => {
    if (!ehListagem) return;
    const id = setInterval(() => {
      if (modalAbertoRef.current) return; // não derruba um cadastro em andamento
      window.location.reload();
    }, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [ehListagem]);
}

export default function Layout({ children }) {
  const { usuario, logout, isAdmin, isProprietario } = useAuth();
  const { tema, setTema } = useTheme();
  useAutoRefresh();
  const nav = NAV_TODOS.filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.mecanicoOnly && isAdmin) return false;
    return true;
  });
  const navigate = useNavigate();
  const [menuAberto, setMenuAberto] = useState(false);
  const tabletPaisagem = useTabletPaisagem();
  // Proprietário não tem etapa de aprovação interna — alertas de "OS aguardando aprovação" não se aplicam
  const pendentes = useAguardandoOrcamento(isAdmin && !isProprietario);
  const pagamentosPendentes = usePagamentosPendentes(isAdmin);
  useAlertaMecanico(isAdmin, usuario);
  const [nomeOficina, setNomeOficina] = useState('');
  useEffect(() => {
    api.get('/configuracao').then(r => setNomeOficina(r.data?.nomeOficina || '')).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  const renderNavItem = (item, mobile = false) => {
    const isPainel = item.to === '/';
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={isPainel}
        onClick={mobile ? () => setMenuAberto(false) : undefined}
        className={({ isActive }) =>
          `flex items-center gap-3 ${mobile ? 'px-5 py-4 text-base' : 'px-5 py-3.5 text-sm'} font-medium transition-colors ${
            isActive
              ? mobile ? 'bg-blue-50 text-blue-700' : 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
              : 'text-gray-600 hover:bg-gray-50'
          }`
        }
      >
        <span className={mobile ? 'text-2xl' : 'text-xl'}>{item.icon}</span>
        <span className="flex-1">{item.label}</span>
        {isPainel && isAdmin && pendentes > 0 && (
          <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
            {pendentes}
          </span>
        )}
        {item.to === '/financeiro' && isAdmin && pagamentosPendentes > 0 && (
          <span className="bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
            {pagamentosPendentes}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-blue-700 text-white shadow-lg sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              className={`text-2xl p-1 rounded-lg hover:bg-blue-600 ${tabletPaisagem ? '' : 'lg:hidden'}`}
              onClick={() => setMenuAberto(!menuAberto)}
            >
              ☰
            </button>
            <span className="font-bold text-lg truncate max-w-[55vw] sm:max-w-none">🔧 {nomeOficina || 'TorqueApp'}</span>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && pendentes > 0 && (
              <div className="flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full animate-pulse">
                📤 {pendentes} OS aguardando
              </div>
            )}
            <span className="text-sm text-blue-200 hidden sm:inline">
              {usuario?.nome}
              {usuario?.perfil === 'mecanico' && <span className="ml-1.5 text-xs bg-blue-600 px-1.5 py-0.5 rounded">Mecânico</span>}
            </span>
            <button
              onClick={() => setTema(tema === 'escuro' ? 'claro' : 'escuro')}
              title={tema === 'escuro' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
              aria-label="Alternar tema"
              className="text-lg bg-blue-600 hover:bg-blue-500 w-9 h-9 rounded-lg transition-colors flex items-center justify-center"
            >
              {tema === 'escuro' ? '☀️' : '🌙'}
            </button>
            <button
              onClick={handleLogout}
              className="text-sm bg-blue-600 hover:bg-blue-500 px-3 py-1.5 rounded-lg transition-colors"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar fixa — some no tablet em paisagem para ganhar espaço horizontal (vira menu hambúrguer) */}
        {!tabletPaisagem && (
          <nav className="hidden lg:flex flex-col w-56 bg-white shadow-sm border-r border-gray-200 pt-4 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto self-start">
            {nav.map(item => renderNavItem(item))}
          </nav>
        )}

        {/* Menu overlay — mobile sempre, e também no tablet em paisagem (sidebar fixa oculta) */}
        {menuAberto && (
          <div className={`${tabletPaisagem ? '' : 'lg:hidden'} fixed inset-0 z-40`} onClick={() => setMenuAberto(false)}>
            <div className="absolute inset-0 bg-black/40" />
            <nav className="absolute left-0 top-14 bottom-0 w-64 bg-white shadow-xl pt-4" onClick={e => e.stopPropagation()}>
              {nav.map(item => renderNavItem(item, true))}
            </nav>
          </div>
        )}

        {/* Conteúdo */}
        <main className="flex-1 p-4 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
